// src/components/attendance/MarkAttendance.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import API from "../../api";
import { listEnrollments } from "../../api/enrollments";
import { attendanceBySession, markAttendance } from "../../api/attendance";
import { getWalkInsBySession, removeWalkInStudent } from "../../api/sessionParticipants";
import { inputLocalToWallTimeString, wallTimeServerToInputValue } from "../../utils/time";
import { formatDateTime, formatLocalDateTime } from "../../utils/date";
import { formatTime, formatDate as formatDateRegional, academyNowDate } from "../../utils/regionalFormat";
import SessionTimePicker, { isoToTimeString } from "../common/SessionTimePicker";
import { getSession, setSessionStatus } from "../../api/sessions";
import UserLink from "../common/UserLink";
import PageHeaderSection from "../layout/PageHeaderSection";
import { ListLoadingSpinner } from "../layout/ListPageLayout";
import AddWalkInStudent from "./AddWalkInStudent";
import CarryForwardCell from "./CarryForwardCell";
import CarryForwardColumnHeader from "./CarryForwardColumnHeader";
import { isPeriodBillingMode } from "../../utils/periodBillingCopy";
import { showConfirm } from '../../utils/popup';
import { studentDisplayName, localizeBatchName } from '../../utils/displayNames';
import { apiErrorMessage } from '../../utils/apiError';

const STATUSES = ["PRESENT", "ABSENT"];

const useDebounced = (value, delay = 200) => {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
};

const nameFromEnrollment = (e) => {
    return e.studentName || e.name || e.username || "";
};

const MarkAttendance = ({ role = "ADMIN" }) => {
    const { t } = useTranslation();
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [warning, setWarning] = useState("");
    const [rosterLoadError, setRosterLoadError] = useState(false);
    const [savedAck, setSavedAck] = useState("");
    const [query, setQuery] = useState("");
    const dq = useDebounced(query, 200);

    // Walk-in students state
    const [walkIns, setWalkIns] = useState([]);
    const [walkInsLoadError, setWalkInsLoadError] = useState(false);
    const [showAddWalkIn, setShowAddWalkIn] = useState(false);

    const [sessionNotes, setSessionNotes] = useState(
        (location.state && typeof location.state.sessionNotes === "string"
            ? location.state.sessionNotes
            : ""));

    const sessionId =
        location.state?.sessionId ??
        Number(params?.sessionId ?? params?.id ?? location.state?.id ?? 0);

    const [batchId, setBatchId] = useState(
        location.state?.batchId ??
        Number(params?.batchId ?? params?.batch_id ?? location.state?.batchId ?? 0)
    );

    const rolePath = role?.toLowerCase?.() || "admin";

    const [meta, setMeta] = useState(() => ({
        levelName:
            location.state?.levelName ??
            location.state?.level?.name ??
            "",
        batchName:
            location.state?.batchName ??
            location.state?.batch?.name ??
            "",
        coachFullName:
            location.state?.coachFullName ??
            location.state?.coach?.coachFullName ??
            location.state?.coach?.name ??
            "",
        coachId:
            location.state?.coachId ?? null,
        status:
            location.state?.status ?? "",
        startTime:
            location.state?.startTime ?? "",
        endTime:
            location.state?.endTime ?? "",
        carryForward:
            location.state?.carryForward ?? false,
        batchCarryForwardMode:
            location.state?.batchCarryForwardMode ?? null,
        batchChargesMode:
            location.state?.batchChargesMode ?? null,
    }));
    const [sessionMetaWarning, setSessionMetaWarning] = useState("");

    useEffect(() => {
        let alive = true;
        const needsFetch =
            !!sessionId &&
            (!meta.levelName || !meta.batchName || !meta.coachFullName || !meta.status || !meta.startTime || !meta.endTime);

        if (!needsFetch && batchId) return;

        (async () => {
            try {
                const res = await getSession(role, sessionId);
                const d = res?.data ?? {};

                if (d.sessionNotes != null && !sessionNotes) {
                    setSessionNotes(d.sessionNotes);
                }
                // Resolve batchId from API if missing from state/params
                if (!batchId && d.batchId) {
                    setBatchId(Number(d.batchId));
                }
                const next = {
                    levelName: meta.levelName || d.levelName || d.level?.name || "",
                    batchName: meta.batchName || d.batchName || d.batch?.name || "",
                    coachFullName:
                        meta.coachFullName ||
                        d.coachFullName ||
                        d.coach?.coachFullName ||
                        d.coach?.name ||
                        "",
                    coachId: meta.coachId || d.coach?.id || d.coachId || d.coach?.coachId || null,
                    status: meta.status || d.status || "",
                    startTime: meta.startTime || d.startTime || "",
                    endTime: meta.endTime || d.endTime || "",
                    carryForward: meta.carryForward !== false ? (meta.carryForward || d.carryForward || d.batch?.carryForward || false) : false,
                    batchCarryForwardMode: d.batchCarryForwardMode || null,
                    batchChargesMode: d.batchChargesMode || d.batch?.chargesMode || null,
                };
                if (alive) {
                    setMeta(next);
                    setSessionMetaWarning("");
                }
            } catch (e) {
                console.warn("getSession fallback failed", e);
                if (alive) {
                    setSessionMetaWarning(t("attendance.sessionDetailsLoadFailed", {
                        defaultValue: "Could not load session details. Roster may still work; refresh to retry.",
                    }));
                }
            }
        })();

        return () => {
            alive = false;
        };
    }, [sessionId, role, sessionNotes]);

    useEffect(() => {
        const load = async () => {
            if (!sessionId) {
                setWarning(t("attendance.sessionIdMissing"));
                setLoading(false);
                return;
            }
            setLoading(true);
            setWarning("");
            setRosterLoadError(false);

            try {
                const { data: existing = [] } = await attendanceBySession(role, sessionId);
                const byStudent = new Map(
                    existing.map((a) => [
                        a.studentId,
                        {
                            studentId: a.studentId,
                            attendanceType: a.attendanceType || "",
                            // Use checkInTime/checkOutTime for actual attendance times (for billing)
                            startTime: wallTimeServerToInputValue(a.checkInTime),
                            endTime: wallTimeServerToInputValue(a.checkOutTime),
                        },
                    ])
                );

                let roster = [];
                if (batchId) {
                    try {
                        // Try new session-specific endpoint first (for walk-in sessions)
                        if (sessionId) {
                            const baseRole = role === "ADMIN" ? "admin" : role === "MANAGER" ? "manager" : "coach";
                            const enrRes = await API.get(`/${baseRole}/sessions/${sessionId}/enrollments`);
                            const enr = Array.isArray(enrRes?.data) ? enrRes.data : enrRes?.data?.content || [];

                            if (enr.length > 0) {
                                roster = enr
                                    .filter((e) => !e.isWalkIn)
                                    .map((e) => {
                                        const sid = e.studentId ?? e.userId ?? e.id;
                                        if (sid == null) return null;
                                        return {
                                            studentId: sid,
                                            studentName: nameFromEnrollment(e) || studentDisplayName(null, sid, t),
                                            studentFullName: nameFromEnrollment(e) || studentDisplayName(null, sid, t),
                                            overrideCarryForward: e.overrideCarryForward,
                                        };
                                    })
                                    .filter(Boolean);

                                const seen = new Set();
                                roster = roster.filter((r) => {
                                    if (seen.has(r.studentId)) return false;
                                    seen.add(r.studentId);
                                    return true;
                                });
                            }
                        }
                    } catch (err) {
                        // Fallback to batch-based enrollment if session endpoint fails
                        console.log("Session endpoint failed, falling back to batch enrollments", err);
                    }

                    // If no roster from session endpoint, use batch enrollments (original logic)
                    if (roster.length === 0) {
                        const enrRes = await listEnrollments(role, { batchId, page: 0, size: 500 });
                        const enr = Array.isArray(enrRes?.data?.content) ? enrRes.data.content : enrRes?.data || [];

                        roster = enr
                            .map((e) => {
                                const sid = e.studentId ?? e.userId ?? e.id;
                                if (sid == null) return null;
                                return {
                                    studentId: sid,
                                    studentName: nameFromEnrollment(e) || studentDisplayName(null, sid, t),
                                    studentFullName: nameFromEnrollment(e) || studentDisplayName(null, sid, t),
                                    overrideCarryForward: e.overrideCarryForward,
                                };
                            })
                            .filter(Boolean);
                    }
                } else {
                    setWarning(t("attendance.batchUnknown"));
                }

                const merged = [];
                if (roster.length > 0) {
                    for (const r of roster) {
                        const m = byStudent.get(r.studentId) || {
                            studentId: r.studentId,
                            attendanceType: "",
                            startTime: "",
                            endTime: "",
                        };
                        merged.push({
                            ...m,
                            studentName: r.studentName,
                            studentFullName: r.studentFullName || r.studentName,
                            overrideCarryForward: r.overrideCarryForward,
                        });
                        byStudent.delete(r.studentId);
                    }
                } else {
                    for (const [sid, m] of byStudent.entries()) {
                        merged.push({
                            ...m,
                            studentName: studentDisplayName(null, sid, t),
                            studentFullName: studentDisplayName(null, sid, t),
                        });
                    }
                }

                setRows(merged);
            } catch (e) {
                console.error(e);
                setRows([]);
                setRosterLoadError(true);
                setWarning(t("attendance.loadFailed"));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [sessionId, batchId, role, t]);

    // Load walk-in students
    useEffect(() => {
        const loadWalkIns = async () => {
            if (!sessionId) return;
            try {
                const { data } = await getWalkInsBySession(role, sessionId);
                setWalkIns(data || []);
                setWalkInsLoadError(false);
            } catch (err) {
                console.error('Failed to load walk-in students:', err);
                setWalkIns([]);
                setWalkInsLoadError(true);
            }
        };
        loadWalkIns();
    }, [sessionId, role]);

    const tokens = useMemo(() => {
        return dq.toLowerCase().split(/\s+/).filter(Boolean);
    }, [dq]);

    const filtered = useMemo(() => {
        if (!tokens.length) return rows;
        return rows.filter((r) => {
            const hay = `${r.studentFullName || r.studentName} #${r.studentId}`.toLowerCase();
            return tokens.every((t) => hay.includes(t));
        });
    }, [rows, dq, tokens]);

    const rosterStudentIds = useMemo(
        () => new Set(rows.map((r) => Number(r.studentId)).filter(Boolean)),
        [rows]
    );

    const excludedWalkInStudentIds = useMemo(() => {
        const ids = new Set(rosterStudentIds);
        walkIns.forEach((w) => {
            if (w.studentId != null) ids.add(Number(w.studentId));
        });
        return [...ids];
    }, [rosterStudentIds, walkIns]);

    const visibleWalkIns = useMemo(
        () => walkIns.filter((w) => !rosterStudentIds.has(Number(w.studentId))),
        [walkIns, rosterStudentIds]
    );

    const duplicateWalkIns = useMemo(
        () => walkIns.filter((w) => rosterStudentIds.has(Number(w.studentId))),
        [walkIns, rosterStudentIds]
    );

    const setAll = (type) => setRows((prev) => prev.map((r) => ({ ...r, attendanceType: type })));
    const updateRow = (studentId, patch) =>
        setRows((prev) => prev.map((r) => (r.studentId === studentId ? { ...r, ...patch } : r)));

    // Walk-in handlers
    const handleWalkInSuccess = async () => {
        setShowAddWalkIn(false);
        // Reload walk-ins
        try {
            const { data } = await getWalkInsBySession(role, sessionId);
            setWalkIns(data || []);
            setSavedAck(t("attendance.walkInAdded"));
            setTimeout(() => setSavedAck(""), 6000);
        } catch (err) {
            console.error('Failed to reload walk-ins:', err);
        }
    };

    const handleRemoveWalkIn = async (studentId) => {
        if (!await showConfirm(t("attendance.removeWalkInConfirm"))) {
            return;
        }
        try {
            await removeWalkInStudent(role, sessionId, studentId);
            const { data } = await getWalkInsBySession(role, sessionId);
            setWalkIns(data || []);
            setSavedAck(t("attendance.walkInRemoved"));
            setTimeout(() => setSavedAck(""), 6000);
        } catch (err) {
            console.error('Failed to remove walk-in:', err);
            setWarning(t("attendance.removeWalkInFailed"));
        }
    };

    const hasAnyAttendance = async () => {
        try {
            const res = await attendanceBySession(role, sessionId);
            const data = res?.data;
            if (Array.isArray(data) && data.length > 0) return true;
            if (Array.isArray(data?.content) && data.content.length > 0) return true;
            return false;
        } catch (e) {
            console.error(e);
            return null;
        }
    };

    const onSave = async () => {
        const status = (meta.status || "").toString().toUpperCase();
        if (status === "COMPLETED" || status === "CANCELLED") return;
        if (!sessionId) return;
        if (!sessionNotes || !sessionNotes.trim()) {
            setWarning(t("attendance.notesRequiredSave"));
            return;
        }
        setSaving(true);
        try {
            const payloadRows = rows.map((r) => ({
                studentId: r.studentId,
                attendanceType: r.attendanceType || "",
                startTime: inputLocalToWallTimeString(r.startTime),
                endTime: inputLocalToWallTimeString(r.endTime),
            }));
            await markAttendance(role, sessionId, payloadRows, meta.coachId || null, sessionNotes.trim());
            setWarning("");
            const ts = academyNowDate();
            setSavedAck(t("attendance.savedAck", { time: formatLocalDateTime(ts) }));
            setTimeout(() => setSavedAck(""), 6000);
        } catch (e) {
            console.error(e);
            setWarning(t("attendance.saveFailed"));
        } finally {
            setSaving(false);
        }
    };

    const onCompleteSession = async () => {
        const ok = await hasAnyAttendance();
        if (ok === null) {
            setWarning(t("attendance.verifyAttendanceFailed", {
                defaultValue: "Could not verify attendance. Complete is blocked until this succeeds — refresh and try again.",
            }));
            return;
        }
        if (!ok) {
            setWarning(t("attendance.markBeforeComplete"));
            return;
        }

        if (!sessionNotes || !sessionNotes.trim()) {
            setWarning(t("attendance.notesRequiredComplete"));
            return;
        }

        if (!await showConfirm(t('attendance.completeConfirm'))) {
            return;
        }

        setSaving(true);
        try {
            const payloadRows = rows.map((r) => ({
                studentId: r.studentId,
                attendanceType: r.attendanceType || "",
                startTime: inputLocalToWallTimeString(r.startTime),
                endTime: inputLocalToWallTimeString(r.endTime),
            }));

            await markAttendance(role, sessionId, payloadRows, meta.coachId || null, sessionNotes.trim());
            await setSessionStatus(role, sessionId, "COMPLETED");

            alert(t("attendance.completeSuccess"));
            const returnTo = location.state?.returnTo || `/${rolePath}/sessions`;
            navigate(returnTo);
        } catch (e) {
            console.error(e);
            setWarning(t("attendance.completeFailed", {
                message: apiErrorMessage(e, t('common.unknownError')),
            }));
        } finally {
            setSaving(false);
        }
    };

    const startLabel = meta.startTime ? formatDateTime(meta.startTime) : "—";
    const endLabel = meta.endTime ? formatDateTime(meta.endTime) : "—";
    const statusRaw = (meta.status || "").toString().toUpperCase();
    const statusLabel = statusRaw === "PENDING"
        ? t("sessions.statusPending")
        : statusRaw === "COMPLETED"
            ? t("sessions.statusCompleted")
            : statusRaw === "CANCELLED"
                ? t("sessions.statusCancelled")
                : (meta.status || "—");
    const isCompleted = statusRaw === "COMPLETED";
    const isCancelled = statusRaw === "CANCELLED";
    const formatTimeOnly = (iso) => (iso ? formatTime(iso) : "—");
    const formatDate = (iso) => (iso ? formatDateRegional(iso) : "—");

    const startTime = formatTimeOnly(meta.startTime);
    const endTime = formatTimeOnly(meta.endTime);
    const startDate = formatDate(meta.startTime);
    const endDate = formatDate(meta.endTime);

    // Show one date if same day, else show both dates
    const timingsDisplay = startDate === endDate
        ? `${startDate} • ${startTime} → ${endTime}`
        : `${startDate} ${startTime} → ${endDate} ${endTime}`;

    return (
        <div className="list-page">
            <PageHeaderSection
                title={t("attendance.markTitle")}
                subtitle={`${meta.levelName || "—"} • ${localizeBatchName(meta.batchName, t) || "—"} — ${timingsDisplay}`}
                aside={(
                    <>
                        <button type="button" disabled={isCompleted || isCancelled} className="btn-page-action-primary" onClick={() => setAll("PRESENT")}>
                            {t("attendance.markAllPresent")}
                        </button>
                        <button type="button" disabled={isCompleted || isCancelled} className="btn-page-action-danger" onClick={() => setAll("ABSENT")}>
                            {t("attendance.markAllAbsent")}
                        </button>
                        <button
                            type="button"
                            disabled={saving || isCompleted || isCancelled}
                            className="btn-page-action-info"
                            onClick={onSave}
                        >
                            {saving ? t("attendance.saving") : t("attendance.saveAttendance")}
                        </button>
                        <button
                            type="button"
                            disabled={saving || isCompleted || isCancelled}
                            className="btn-page-action-warning"
                            onClick={onCompleteSession}
                            title={
                                isCompleted
                                    ? t("attendance.alreadyCompleted")
                                    : isCancelled
                                        ? t("attendance.cancelledCannotComplete")
                                        : t("attendance.completeTitle")
                            }
                        >
                            {saving ? t("attendance.completing") : t("attendance.completeSession")}
                        </button>
                    </>
                )}
            >
                <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                    <div className="space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("attendance.coach")}</p>
                        <p className="text-sm font-semibold text-slate-900">
                            <UserLink userId={meta.coachId} name={meta.coachFullName || "—"} role="COACH" />
                        </p>
                    </div>
                    <div className="space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("attendance.status")}</p>
                        <p className={`inline-block rounded-md px-3 py-1 text-sm font-bold ${statusRaw === "COMPLETED" ? "bg-green-100 text-green-800" :
                            statusRaw === "CANCELLED" ? "bg-red-100 text-red-800" :
                                statusRaw === "PENDING" ? "bg-yellow-100 text-yellow-800" :
                                    "bg-gray-100 text-gray-800"
                            }`}>
                            {statusLabel || "—"}
                        </p>
                    </div>
                </div>
            </PageHeaderSection>

            {sessionMetaWarning && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                    {sessionMetaWarning}
                </div>
            )}

            <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
                <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t("attendance.sessionNotesRequired")} <span className="text-red-500 font-bold">*</span>
                    </label>
                    <textarea
                        className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-800 focus:border-blue-500 focus:outline-none transition-colors"
                        rows={3}
                        value={sessionNotes}
                        onChange={(e) => setSessionNotes(e.target.value)}
                        placeholder={t("attendance.sessionNotesPlaceholder")}
                        disabled={isCompleted || isCancelled}
                    />
                    <p className="text-xs text-gray-500 mt-1">{t("attendance.sessionNotesHint")}</p>
                </div>

                <div className="mb-4">
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("attendance.searchStudents")}
                        className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-colors"
                    />
                </div>

                {warning && (
                    <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 text-sm text-red-800 mb-4">
                        <p className="font-semibold">⚠️ {t("attendance.warning")}</p>
                        <p>{warning}</p>
                    </div>
                )}

                {savedAck && (
                    <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 text-sm text-green-800 mb-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="font-semibold">✓ {t("attendance.success")}</p>
                                <p>{savedAck}</p>
                            </div>
                            <button
                                className="text-green-700 hover:text-green-900 font-bold"
                                onClick={() => setSavedAck("")}
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <ListLoadingSpinner label={t("attendance.loadingStudents")} />
                ) : (
                    <>
                        {(() => {
                            const showCarryForward = !isPeriodBillingMode(meta.batchChargesMode);
                            const tableColSpan = showCarryForward ? 5 : 4;
                            return (
                        <>
                        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 text-sm text-blue-800 mb-4">
                            <p className="font-semibold">ℹ️ {t("attendance.checkInOutInfo")}</p>
                            <p>{t("attendance.checkInOutHint")}</p>
                        </div>
                        <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full text-sm">
                                <thead className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                                    <tr>
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.student")}</th>
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.attendanceStatus")}</th>
                                        {showCarryForward && (
                                            <CarryForwardColumnHeader className="px-4 py-3 text-center font-bold text-gray-900" />
                                        )}
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.checkIn")}</th>
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.checkOut")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 && !rosterLoadError ? (
                                        <tr>
                                            <td className="px-4 py-8 text-center text-gray-500 italic" colSpan={tableColSpan}>
                                                {t("attendance.noStudentsMatch", { query: dq })}
                                            </td>
                                        </tr>
                                    ) : (
                                        filtered.map((r, idx) => {
                                            // Determine if check-in/check-out should be shown based on per-student carry forward settings
                                            const showCheckInOut = (() => {
                                                // If student has explicit override, use that
                                                if (r.overrideCarryForward === true) return true;
                                                if (r.overrideCarryForward === false) return false;

                                                // If no override (null/undefined), check batch policy
                                                if (r.overrideCarryForward === null || r.overrideCarryForward === undefined) {
                                                    return meta.batchCarryForwardMode === 'ALLOWED';
                                                }

                                                return false;
                                            })();

                                            // Determine row background color based on attendance status
                                            const getRowBgColor = () => {
                                                if (r.attendanceType === 'PRESENT') return 'bg-green-200 hover:bg-green-300';
                                                if (r.attendanceType === 'ABSENT') return 'bg-red-200 hover:bg-red-300';
                                                return idx % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50';
                                            };
                                            return (
                                                <tr key={r.studentId} className={`border-t ${getRowBgColor()} transition-colors`}>
                                                    <td className="px-4 py-3 text-center font-medium">
                                                        <UserLink
                                                            userId={r.studentId}
                                                            name={r.studentName || r.studentFullName || studentDisplayName(null, r.studentId, t)}
                                                            role="STUDENT"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <select
                                                            disabled={isCompleted || isCancelled}
                                                            className="border-2 border-gray-300 rounded-lg px-2 py-1 font-medium focus:border-blue-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                                                            value={r.attendanceType || ""}
                                                            onChange={(e) => updateRow(r.studentId, { attendanceType: e.target.value })}
                                                        >
                                                            <option value="">{t("attendance.notMarked")}</option>
                                                            <option value="PRESENT">{t("attendance.present")}</option>
                                                            <option value="ABSENT">{t("attendance.absent")}</option>
                                                        </select>
                                                    </td>
                                                    {showCarryForward && (
                                                    <td className="px-4 py-3 text-center">
                                                        <CarryForwardCell
                                                            batchMode={meta.batchCarryForwardMode}
                                                            overrideCarryForward={r.overrideCarryForward}
                                                            emphasized
                                                        />
                                                    </td>
                                                    )}
                                                    <td className="px-4 py-3 text-center">
                                                        {!showCheckInOut ? (
                                                            <span className="text-gray-400 italic">{t("attendance.notApplicable")}</span>
                                                        ) : (
                                                            <SessionTimePicker
                                                                value={r.startTime || ''}
                                                                onChange={(newValue) => {
                                                                    const patch = { startTime: newValue };
                                                                    if (r.endTime && newValue) {
                                                                        const inHhMm = isoToTimeString(newValue);
                                                                        const outHhMm = isoToTimeString(r.endTime);
                                                                        if (inHhMm && outHhMm && outHhMm <= inHhMm) {
                                                                            patch.endTime = "";
                                                                        }
                                                                    }
                                                                    updateRow(r.studentId, patch);
                                                                }}
                                                                sessionDate={meta.startTime}
                                                                sessionStartTime={meta.startTime}
                                                                sessionEndTime={meta.endTime}
                                                                maxTime={isoToTimeString(r.endTime) || null}
                                                                placeholder={t("attendance.selectCheckIn")}
                                                                className="border-2 border-gray-300 rounded-lg px-2 py-1 text-sm focus:border-blue-500 focus:outline-none w-40"
                                                            />
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {!showCheckInOut ? (
                                                            <span className="text-gray-400 italic">{t("attendance.notApplicable")}</span>
                                                        ) : (
                                                            <SessionTimePicker
                                                                value={r.endTime || ''}
                                                                onChange={(newValue) => updateRow(r.studentId, { endTime: newValue })}
                                                                sessionDate={meta.startTime}
                                                                sessionStartTime={meta.startTime}
                                                                sessionEndTime={meta.endTime}
                                                                minTime={isoToTimeString(r.startTime) || null}
                                                                placeholder={t("attendance.selectCheckOut")}
                                                                className="border-2 border-gray-300 rounded-lg px-2 py-1 text-sm focus:border-blue-500 focus:outline-none w-40"
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                        </>
                            );
                        })()}
                    </>
                )}

                {/* Walk-in Students Section */}
                <div className="mt-8 border-t-4 border-blue-200 pt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-800">
                            🚶 {t("attendance.walkInStudents", { count: visibleWalkIns.length })}
                        </h3>
                        <button
                            onClick={() => setShowAddWalkIn(true)}
                            disabled={isCompleted || isCancelled}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {t("attendance.addWalkIn")}
                        </button>
                    </div>

                    {duplicateWalkIns.length > 0 && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                            <p className="font-medium">
                                {t("attendance.duplicateWalkIns", { count: duplicateWalkIns.length })}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {duplicateWalkIns.map((walkIn) => (
                                    <button
                                        key={walkIn.id}
                                        type="button"
                                        onClick={() => handleRemoveWalkIn(walkIn.studentId)}
                                        disabled={isCompleted || isCancelled}
                                        className="px-3 py-1 rounded bg-amber-100 border border-amber-300 text-amber-900 hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {t("attendance.removeDuplicate", { name: walkIn.studentName || studentDisplayName(null, walkIn.studentId, t) })}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {walkInsLoadError ? (
                        <div className="text-center py-8 bg-rose-50 rounded-lg border border-rose-200" role="status">
                            <p className="text-rose-700">{t("attendance.walkInsLoadFailed")}</p>
                        </div>
                    ) : visibleWalkIns.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-gray-500">{t("attendance.noWalkIns")}</p>
                            <p className="text-sm text-gray-400 mt-1">
                                {t("attendance.walkInHint")}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full text-sm">
                                <thead className="bg-gradient-to-r from-purple-100 to-purple-50 border-b-2 border-purple-300">
                                    <tr>
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.student")}</th>
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.status")}</th>
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.checkIn")}</th>
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.checkOut")}</th>
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.notes")}</th>
                                        <th className="px-4 py-3 text-center font-bold text-gray-900">{t("attendance.actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleWalkIns.map((walkIn) => (
                                        <tr key={walkIn.id} className="border-t bg-green-200 hover:bg-green-300 transition-colors">
                                            <td className="px-4 py-3 text-center font-medium">
                                                <UserLink
                                                    userId={walkIn.studentId}
                                                    name={walkIn.studentName || studentDisplayName(null, walkIn.studentId, t)}
                                                    role="STUDENT"
                                                />
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
                                                    {t("attendance.present")}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center text-gray-700">
                                                {walkIn.checkinTime || <span className="text-gray-400 italic">{t("attendance.sessionStart")}</span>}
                                            </td>
                                            <td className="px-4 py-3 text-center text-gray-700">
                                                {walkIn.checkoutTime || <span className="text-gray-400 italic">{t("attendance.sessionEnd")}</span>}
                                            </td>
                                            <td className="px-4 py-3 text-center text-gray-600 text-xs max-w-xs truncate">
                                                {walkIn.notes || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => handleRemoveWalkIn(walkIn.studentId)}
                                                    disabled={isCompleted || isCancelled}
                                                    className="text-red-600 hover:text-red-800 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {t("attendance.remove")}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Walk-in Student Modal */}
            {showAddWalkIn && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <AddWalkInStudent
                        role={role}
                        sessionId={sessionId}
                        batchId={batchId}
                        excludedStudentIds={excludedWalkInStudentIds}
                        onSuccess={handleWalkInSuccess}
                        onCancel={() => setShowAddWalkIn(false)}
                    />
                </div>
            )}
        </div>
    );
};

export default MarkAttendance;
